<template>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div class="md:col-span-2">
            <label class="mb-1 block text-sm font-medium text-gray-700">
                {{ $t("checkout.emergency.name") }} <span class="text-red-500">*</span>
            </label>
            <input
                data-testid="emergency-name"
                :value="modelValue.name"
                type="text"
                class="w-full rounded-lg border px-3 py-2 text-sm"
                :class="errors.name ? 'border-red-400' : 'border-gray-300'"
                @input="emitField('name', $event.target.value)"
            />
            <p v-if="errors.name" class="mt-1 text-xs text-red-600">{{ errors.name }}</p>
        </div>

        <div class="md:col-span-2">
            <label class="mb-1 block text-sm font-medium text-gray-700">
                {{ $t("checkout.emergency.phone") }} <span class="text-red-500">*</span>
            </label>
            <MazPhoneNumberInput
                data-testid="emergency-phone"
                v-model="phoneInputValue"
                v-model:country-code="countryCode"
                show-code-on-list
                :defaultCountryCode="'ID'"
                :preferred-countries="[
                    'ID',
                    'MY',
                    'SG',
                    'US',
                    'TH',
                    'SA',
                    'FR',
                    'AU',
                ]"
                :ignored-countries="['AC']"
                :countrySelectorDisplayName="isMobileDevice"
                :countrySelectorWidth="isMobileDevice ? '10.5rem' : '7.5rem'"
                :translations="phoneTranslations"
                orientation="row"
                :block="true"
                noValidationSuccess
                noExample
                class="w-full full-width-fix phone-input"
                style="width: 100%; max-width: none"
                :class="[errors.phone ? 'phone-input-error' : '', isMobileDevice ? 'phone-input--with-code' : '']"
                @update="onPhoneNumberUpdate"
            >
                <!-- Selektor tertutup hanya sanggup menampilkan SALAH SATU: kode ATAU nama negara
                     (maz-ui: option-input-value-key). Mobile butuh dua-duanya, jadi kodenya ditempel
                     di tombol bendera — pola yang sama dengan field pemesan di Payment.vue. -->
                <template #selector-flag="{ countryCode: selectedCountry }">
                    <span class="flex items-center gap-1">
                        <img
                            :src="`https://flagcdn.com/h20/${String(selectedCountry || '').toLowerCase()}.png`"
                            :alt="selectedCountry"
                            width="20"
                            height="15"
                            class="h-4 w-5 rounded-sm"
                        />
                        <span v-if="isMobileDevice" class="text-sm text-gray-700">
                            {{ countryDialCode(selectedCountry) }}
                        </span>
                    </span>
                </template>
            </MazPhoneNumberInput>
            <p v-if="errors.phone" class="mt-1 text-xs text-red-600">{{ errors.phone }}</p>
        </div>

        <div :class="modelValue.relation === 'OTHER' ? 'md:col-span-1' : 'md:col-span-2'">
            <label class="mb-1 block text-sm font-medium text-gray-700">
                {{ $t("checkout.emergency.relation") }} <span class="text-red-500">*</span>
            </label>
            <select
                data-testid="emergency-relation"
                :value="modelValue.relation"
                class="w-full rounded-lg border px-3 py-2 text-sm"
                :class="errors.relation ? 'border-red-400' : 'border-gray-300'"
                @change="emitField('relation', $event.target.value)"
            >
                <option value="">{{ $t("checkout.emergency.selectRelation") }}</option>
                <option value="PARENT">{{ $t("profilePage.relationOptions.parent") }}</option>
                <option value="SIBLING">{{ $t("profilePage.relationOptions.sibling") }}</option>
                <option value="SPOUSE">{{ $t("profilePage.relationOptions.spouse") }}</option>
                <option value="FRIEND">{{ $t("profilePage.relationOptions.friend") }}</option>
                <option value="OTHER">{{ $t("profilePage.relationOptions.other") }}</option>
            </select>
            <p v-if="errors.relation" class="mt-1 text-xs text-red-600">{{ errors.relation }}</p>
        </div>

        <div v-if="modelValue.relation === 'OTHER'" class="md:col-span-1">
            <label class="mb-1 block text-sm font-medium text-gray-700">
                {{ $t("checkout.emergency.relationOther") }} <span class="text-red-500">*</span>
            </label>
            <input
                data-testid="emergency-relation-other"
                :value="modelValue.relationOther"
                type="text"
                class="w-full rounded-lg border px-3 py-2 text-sm"
                :class="errors.relationOther ? 'border-red-400' : 'border-gray-300'"
                @input="emitField('relationOther', $event.target.value)"
            />
            <p v-if="errors.relationOther" class="mt-1 text-xs text-red-600">{{ errors.relationOther }}</p>
        </div>
    </div>
</template>

<script>
import { defineAsyncComponent } from "vue";
import detectDeviceMixin from "@/mixins/detectDeviceMixin";
import { dialCodeOf } from "@/utils/phoneValidation.mjs";
// maz-ui + libphonenumber-js (~405 KB) ikut chunk AWAL selama impornya statis: ketiga
// komponen ini terdaftar global lewat require.context di components/modules/index.js.
// Dimuat saat dipakai saja — semuanya field di form booking/profil, bukan halaman depan.
// ponytail: kelambanan ditaruh di tempat impor beratnya, bukan sebagai daftar-nama di
// index.js — komponen maz-ui berikutnya tinggal ikut pola ini, tak ada daftar yang basi.

export default {
    name: "EmergencyContactFields",
    components: {
        MazPhoneNumberInput: defineAsyncComponent(() =>
            import("maz-ui/components/MazPhoneNumberInput")
        ),
    },
    mixins: [detectDeviceMixin],
    props: {
        modelValue: {
            type: Object,
            default: () => ({
                name: "",
                phone: "",
                relation: "",
                relationOther: "",
            }),
        },
        errors: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            phoneInputValue: "",
            countryCode: "ID",
        };
    },
    computed: {
        // computed, bukan data(): teksnya harus ikut berubah saat bahasa diganti
        phoneTranslations() {
            return {
                countrySelector: {
                    placeholder: "",
                    error: this.$t("chooseCountry"),
                    searchPlaceholder: this.$t("searchCountry"),
                },
                phoneInput: { placeholder: "", example: "" },
            };
        },
    },
    watch: {
        "modelValue.phone": {
            immediate: true,
            handler(newPhone) {
                this.phoneInputValue = newPhone || "";
                this.countryCode = this.detectCountryCode(newPhone);
            },
        },
    },
    methods: {
        // Pembungkus tipis supaya util bisa dipanggil dari template.
        countryDialCode(iso2) {
            return dialCodeOf(iso2);
        },
        detectCountryCode(phone) {
            const value = String(phone || "");
            if (value.startsWith("+60")) return "MY";
            if (value.startsWith("+65")) return "SG";
            if (value.startsWith("+1")) return "US";
            if (value.startsWith("+66")) return "TH";
            if (value.startsWith("+61")) return "AU";
            if (value.startsWith("+33")) return "FR";
            if (value.startsWith("+966")) return "SA";
            if (value.startsWith("+62")) return "ID";
            return "ID";
        },
        onPhoneNumberUpdate(payload) {
            const nextValue = payload?.isValid
                ? payload.e164
                : this.phoneInputValue || "";
            this.emitField("phone", nextValue);
        },
        emitField(key, value) {
            const next = {
                ...this.modelValue,
                [key]: value,
            };

            // reset relationOther ketika bukan OTHER
            if (key === "relation" && value !== "OTHER") {
                next.relationOther = "";
            }

            this.$emit("update:modelValue", next);
        },
    },
};
</script>

<style scoped>
::v-deep(.phone-input .m-input-wrapper) {
    border-width: 1px !important;
    border-color: #cbd5e1 !important;
}

::v-deep(.phone-input.m-phone-number-input) {
    display: flex !important;
    width: 100% !important;
    min-width: 100% !important;
    max-width: 100% !important;
    flex: 1 1 100% !important;
}

::v-deep(.phone-input .m-country-selector) {
    flex: 0 0 7.5rem !important;
    min-width: 7.5rem !important;
    width: 7.5rem !important;
}

::v-deep(.phone-input .m-country-selector__select) {
    min-width: 7.5rem !important;
    width: 7.5rem !important;
}

::v-deep(.phone-input .m-phone-input) {
    flex: 1 1 calc(100% - 7.5rem) !important;
    min-width: 0 !important;
    width: calc(100% - 7.5rem) !important;
    max-width: none !important;
}

::v-deep(.phone-input.phone-input-error .m-input-wrapper) {
    border-color: #f87171 !important;
}

::v-deep(.m-phone-number-input.--responsive .m-phone-input) {
    width: 100% !important;
    max-width: 100% !important;
}

::v-deep(.phone-input .m-input-wrapper-input) {
    height: 2.5rem !important;
}

::v-deep(.phone-input .m-input-input) {
    font-size: 0.875rem !important;
    line-height: 1.25rem !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
}

::v-deep(.phone-input .m-input.--has-label .m-input-input) {
    padding-top: 0 !important;
}

::v-deep(.phone-input .m-input-label) {
    display: none !important;
}

::v-deep(.phone-input .m-country-selector__country-flag) {
    top: 50% !important;
    transform: translateY(-50%) !important;
}

::v-deep(.phone-input .m-country-selector__country-flag img) {
    vertical-align: middle !important;
}

::v-deep(.full-width-fix .m-input-wrapper) {
  width: 100% !important;
}


/* Mobile: nama negara ikut tampil di selektor (kodenya pindah ke tombol bendera), jadi kotaknya
   melebar penuh dan nomor turun ke bawah — sama persis dengan field pemesan. */
@media (max-width: 768px) {
    ::v-deep(.phone-input.m-phone-number-input) {
        flex-direction: column !important;
        align-items: stretch !important;
    }

    ::v-deep(.phone-input .m-country-selector),
    ::v-deep(.phone-input .m-country-selector__select),
    ::v-deep(.phone-input .m-phone-input) {
        flex: 0 0 auto !important;
        width: 100% !important;
        min-width: 0 !important;
    }

    ::v-deep(.phone-input .m-phone-input) {
        margin: 0.5rem 0 0 !important;
        align-items: stretch !important;
    }
}

/* Aturan maz (`padding-left: 2.5rem !important`) cuma menyediakan ruang untuk bendera, jadi "+62"
   tertimpa nama negara. Selector-nya harus menyertakan rantai milik maz + `.phone-input` dua kali
   supaya menang tanpa bergantung urutan berkas CSS. */
::v-deep(.phone-input.phone-input--with-code .m-country-selector:not(.--no-flags) .m-country-selector__select .m-select-input input) {
    padding-left: 5rem !important;
}

::v-deep(.phone-input--with-code .m-country-selector__country-flag) {
    /* Begitu selektor menampilkan nama negara, maz menambahkan `--should-have-bottom-flag`
       (`bottom: 2px`). Digabung dengan aturan lama di berkas ini (`top: 50%` + `translateY(-50%)`)
       tinggi kotaknya jadi separuh, lalu digeser ke atas setengah tinggi itu — bendera + "+62"
       terlempar KELUAR, di atas garis input. Posisinya di sini ditentukan penuh: rentang penuh,
       tanpa transform, biarkan `align-items: center` bawaan maz yang menengahkan. */
    top: 0 !important;
    bottom: 0 !important;
    transform: none !important;
    /* Bendera + kode selalu satu baris; kalau tidak, kodenya melipat ke bawah bendera. */
    width: max-content;
    flex-wrap: nowrap;
    white-space: nowrap;
}
</style>
